import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { TrackingEvent } from 'webapp/enums/tracking';
import { UserSettingsKey } from 'webapp/enums/user-settings';

import DataManagerAvailableComponent from 'webapp/components/data-manager/available';
import OnboardingCommonBottomComponent from 'webapp/components/onboarding/common/bottom';
import OnboardingCommonTopComponent from 'webapp/components/onboarding/common/top';

import type { SourceType } from 'webapp/models-custom';
import { EXPIRE_WARNING } from 'webapp/models-custom/infolayer-item/base';
import type InfolayerItemGeneral from 'webapp/models-custom/infolayer-item/general';

type Args = Record<string, never>;

// this step is shown acc owners only

export default class OnboardingStepsIntegrationsComponent extends Component {
	@service('account') declare accountService: Service.Account;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('onboarding') declare onboardingService: Service.Onboarding;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('user-settings') declare userSettingsService: Service.UserSettings;

	@tracked private selectedSourceTypesKeys: string[] = [];
	@tracked private selectedSourceTypes: SourceType[] = [];

	infoLayer: InfolayerItemGeneral | null = null;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.analyticsService.page(TrackingEvent.ONBOARDING_SOURCE);

		// https://app.asana.com/1/7582066485656/project/1201600001078393/task/1210696930974829
		void this.userSettingsService.save(UserSettingsKey.ONBOARDING_SELECT_TOOLS_STARTED, true);

		// Preselect source types from public template
		const publicTemplate = this.onboardingService.publicTemplate;
		if (publicTemplate) {
			publicTemplate.specificSourceTypesKeys.forEach((key) => {
				const sourceType = this.mmSourceTypesService.findModelByKey(key);

				if (sourceType) {
					next(() => {
						this.toggleSourceType(sourceType);
					});
				}
			});
		}
	}

	get title(): string {
		return this.onboardingService.templateId
			? 'Connect your data source to get the template'
			: 'Select the tools you want to pull data from';
	}

	get isContinueBtnDisabled(): boolean {
		return this.selectedSourceTypesKeys.length === 0;
	}

	@action
	toggleSourceType(sourceType: SourceType): void {
		const key = sourceType.key;

		if (this.selectedSourceTypesKeys.includes(key)) {
			this.selectedSourceTypes = this.selectedSourceTypes.filter((type) => type.key !== key);
		} else {
			this.selectedSourceTypes = [...this.selectedSourceTypes, sourceType];
		}

		this.selectedSourceTypesKeys = this.selectedSourceTypes.map((type) => type.key);
	}

	@action
	continue(): void {
		if (this.isContinueBtnDisabled) {
			if (!this.infoLayer) {
				this.infoLayer = this.infolayerService.warning('Please select at least one tool');

				setTimeout(() => {
					this.infoLayer = null;
				}, EXPIRE_WARNING);
			}

			return;
		}

		this.analyticsService.event(TrackingEvent.ONBOARDING_SOURCE_CHOSEN, {
			datasources: this.selectedSourceTypesKeys,
			type: this.onboardingService.getType(this.selectedSourceTypes),
			origin: 'onboarding',
		});

		this.mmSourcesService.openResolvePopup({
			model: this.selectedSourceTypes,
			isOnboarding: true,
			onConnect: (sourceData) => {
				this.goForward();
				const source = this.mmSourcesService.findModel(String(sourceData.id))!;
				this.onboardingService.handleConnectedSource(source);
			},
		});

		this.onboardingService.saveConnectSelections(
			this.selectedSourceTypes.map((type) => type.id!),
		);
	}

	@action
	skip(): void {
		this.analyticsService.event(TrackingEvent.ONBOARDING_SKIPPED);
		this.useSampleData();
	}

	private useSampleData() {
		this.analyticsService.event(TrackingEvent.ONBOARDING_SAMPLE_DATA_CONFIRMED);
		void this.onboardingService.useExcelSampleFile();
		this.goForward();
	}

	private goForward() {
		this.onboardingService.nextStep();
		this.onboardingService.setSafeGuardTimeout();
		void this.accountService.saveMetadataProperties({
			sourcesSelection: this.selectedSourceTypesKeys,
		});
	}
	<template>
		<OnboardingCommonTopComponent
			@title={{this.title}}
			@subtitle="We’ll recommend dashboards and metrics based on the tools you choose and connect."
		/>

		<div class="onboarding-main smart-center-pos">
			<div class="container">
				<DataManagerAvailableComponent
					@isOnboarding={{true}}
					@skipOnboarding={{this.skip}}
					@onboardingSelectedSourceTypes={{this.selectedSourceTypesKeys}}
					@toggleSourceTypeForOnboarding={{this.toggleSourceType}}
				/>
			</div>
		</div>

		<OnboardingCommonBottomComponent
			@skipAction={{this.skip}}
			@buttonText="Get started"
			@continueAction={{this.continue}}
			@isButtonDisabled={{this.isContinueBtnDisabled}}
		/>
	</template>
}
